export type AdminRuntimeConfig = {
  apiUrl: string;
  supabaseUrl: string;
  supabasePublishableKey: string;
};

type AdminRuntimeConfigInput = Partial<
  Record<keyof AdminRuntimeConfig, unknown>
>;

let config: AdminRuntimeConfig | null = null;

const DEFAULT_API_URL = "http://localhost:8000/api";

function buildFromViteEnv(): AdminRuntimeConfig {
  return {
    apiUrl: import.meta.env.VITE_JAIN_ELIBRARY_API_URL ?? DEFAULT_API_URL,
    supabaseUrl: import.meta.env.VITE_SUPABASE_URL ?? "",
    supabasePublishableKey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ?? "",
  };
}

function mergeRuntimeConfig(
  base: AdminRuntimeConfig,
  input: AdminRuntimeConfigInput
): AdminRuntimeConfig {
  const next = { ...base };
  if (typeof input.apiUrl === "string" && input.apiUrl.trim()) {
    next.apiUrl = input.apiUrl.trim();
  }
  if (typeof input.supabaseUrl === "string") {
    next.supabaseUrl = input.supabaseUrl;
  }
  if (typeof input.supabasePublishableKey === "string") {
    next.supabasePublishableKey = input.supabasePublishableKey;
  }
  return next;
}

export function isRuntimeConfigLoaded(): boolean {
  return config !== null;
}

export function getRuntimeConfig(): AdminRuntimeConfig {
  if (!config) {
    throw new Error(
      "Runtime config not loaded. Call loadRuntimeConfig() first."
    );
  }
  return config;
}

export async function loadRuntimeConfig(): Promise<AdminRuntimeConfig> {
  if (config) {
    return config;
  }

  const fromEnv = buildFromViteEnv();

  if (import.meta.env.DEV) {
    config = fromEnv;
    return config;
  }

  try {
    const configUrl = `${import.meta.env.BASE_URL}runtime-config.json`.replace(
      /([^:]\/)\/+/g,
      "$1"
    );
    const response = await fetch(configUrl, { cache: "no-store" });
    if (response.ok) {
      const json = (await response.json()) as AdminRuntimeConfigInput;
      config = mergeRuntimeConfig(fromEnv, json);
      return config;
    }
    console.warn(
      `[runtime-config] ${configUrl} returned ${response.status}; using build-time defaults.`
    );
  } catch (error) {
    console.warn("[runtime-config] Failed to load runtime-config.json:", error);
  }

  config = fromEnv;
  return config;
}
